import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircleText, Copy, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Send-invitation flow (admin only): regenerates the tech's password AND fires an SMS
 * with the login URL + phone + fresh password. If SMS fails (Twilio not configured),
 * we still show the password so the admin can dictate it manually.
 */
export default function SendInvitationDialog({ open, onOpenChange, user }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const close = (o) => {
    onOpenChange(o);
    if (!o) { setResult(null); setCopied(false); }
  };

  const send = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await api.post(`/users/${user.id}/send-invitation`);
      setResult(r.data);
      if (r.data.sms_ok) toast.success("Invitation envoyée par SMS");
      else if (r.data.email_ok) toast.success("SMS indisponible — invitation envoyée par email");
      else toast.warning("SMS et email indisponibles — utilise le mot de passe ci-dessous.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copié");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible");
    }
  };

  if (!user) return null;

  const smsPreview = `Bonjour ${user.name}, bienvenue sur BKflow !
Identifiant : ${user.phone}
Mot de passe : [généré à l'envoi]`;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="invitation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="duotone" className="text-[#005CFF]" />
            {result ? "Invitation envoyée" : `Inviter ${user.name}`}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            {result
              ? (result.sms_ok
                ? "Le SMS contient l'URL de connexion + son mot de passe temporaire."
                : result.email_ok
                  ? "Email envoyé avec l'URL de connexion et le mot de passe temporaire."
                  : "SMS et email indisponibles. Transmets ces infos manuellement.")
              : (user.phone
                ? "Un SMS avec le lien de connexion et un nouveau mot de passe sera envoyé au numéro renseigné. Si le SMS échoue, un email prendra le relais."
                : "Un email avec le lien de connexion et un nouveau mot de passe sera envoyé.")}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            <div className="rounded-xl bg-[#005CFF]/10 border border-[#005CFF]/25 p-3 text-xs text-white/80 space-y-1">
              <div className="flex items-center gap-2 text-[#7CB2FF]">
                <ChatCircleText size={14} weight="duotone" />
                <b>{user.phone ? `SMS envoyé à ${user.phone}` : `Email envoyé à ${user.email || "—"}`}</b>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[11px] text-white/60 pt-1">{smsPreview}</pre>
              {user.phone && user.email && (
                <div className="text-[10px] text-white/40 pt-1">
                  Fallback : email vers <b>{user.email}</b> si Twilio est indisponible.
                </div>
              )}
            </div>
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3 text-[11px] text-amber-200 flex gap-2">
              <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
              <div>
                Envoyer une invitation <b>réinitialise</b> le mot de passe actuel de {user.name}. L&apos;ancien deviendra invalide.
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => close(false)} className="flex-1 border border-white/10" data-testid="invitation-cancel">Annuler</Button>
              <Button onClick={send} disabled={busy} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="invitation-send">
                {busy ? "Envoi…" : "Envoyer l'invitation"}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className={`rounded-xl border p-3 flex gap-2 text-xs ${(result.sms_ok || result.email_ok) ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-amber-500/10 border-amber-500/30 text-amber-200"}`}>
              {(result.sms_ok || result.email_ok) ? <CheckCircle size={16} weight="duotone" /> : <Warning size={16} weight="duotone" />}
              <div>
                {result.sms_ok
                  ? <>SMS envoyé à <b>{result.phone}</b>. Le tech peut se connecter dès maintenant.</>
                  : result.email_ok
                    ? <>Email envoyé à <b>{result.email}</b>. Le tech peut se connecter dès maintenant.</>
                    : <>Aucun canal disponible — dicte les infos ci-dessous à {result.user_name}.</>}
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3 space-y-2 text-xs">
              <Row label="URL" value={result.login_url} onCopy={copy} />
              <Row label="Identifiant" value={result.phone || result.email} onCopy={copy} />
              <Row label="Mot de passe" value={result.password} highlight onCopy={copy} />
            </div>

            <div className="text-[10px] text-white/40">
              Enregistré dans le Journal d&apos;audit.
            </div>

            <Button onClick={() => close(false)} className="w-full bg-[#005CFF] hover:bg-[#0047CC]" data-testid="invitation-done">Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, highlight, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] uppercase tracking-widest text-white/40 w-20 shrink-0">{label}</span>
      <code className={`flex-1 font-mono text-xs break-all ${highlight ? "text-emerald-300 font-bold text-sm" : "text-white/85"}`}>{value}</code>
      <button type="button" onClick={() => onCopy(value)} className="text-white/40 hover:text-[#7CB2FF] p-1" title="Copier">
        <Copy size={13} />
      </button>
    </div>
  );
}
